// Captured-booths list. Proves persistence: this reads straight from IndexedDB,
// so after an airplane-mode reload the booths are still here. Each row shows the
// sync badge ("on device" until the sync engine pushes it).
//
// v0.5: combinable tap-chip filters over SHOW / CREATOR / PRIORITY. Filtering is
// client-side over the already-loaded records (no backend); each facet's options
// are derived dynamically from the distinct values actually present, and the
// three facets combine with AND. Tombstoned booths never load here (listBooths
// hides them).
//
// v0.6: a spyglass in the filter row reveals an inline free-text search that
// live-filters across the VISIBLE fields and ANDs with the three dropdowns.
//
// v1.1: a running spend-totals block (per show + grand total), and three distinct
// render MODES so the list stops losing the user's place — see `RenderMode`.

import { listBooths, getPhotosForBooth } from '../db/booths';
import { photoBlob } from '../db/schema';
import type { BoothRecord } from '../db/schema';
import { PRIORITIES } from '../config';
import { fetchPhoto } from '../sync/api';
import { saveListState, getListState, clearListState, type ListViewState } from './listState';
import { computeSpendTotals, money, unparsedNote } from './totals';

type Facet = 'show' | 'creator' | 'priority';

/**
 * How this render should treat the user's place in the list (v1.1):
 *  - 'fresh'    — entering from the Booths tab: All filters, top of the list.
 *  - 'restore'  — coming back from a booth detail: put the filters, the search text
 *                 and the scroll offset back exactly as they were on the way in.
 *  - 'preserve' — a re-render underneath the user (post-sync / post-pull): read the
 *                 live controls + scroll BEFORE the rebuild and put them straight
 *                 back, so a background merge never yanks the page.
 */
export type RenderMode = 'fresh' | 'restore' | 'preserve';

/** The scrolling element is the document (the app bar is sticky, the tab bar fixed). */
function scrollTop(): number {
  return window.scrollY || document.documentElement.scrollTop || 0;
}

function scrollTo(y: number): void {
  window.scrollTo(0, y);
  // The list body is written synchronously, so the document already has its full
  // height — but iOS occasionally applies a scroll set during a hash-change render
  // a frame late. Re-assert on the next frame; a no-op when the first one took.
  requestAnimationFrame(() => window.scrollTo(0, y));
}

/** Read the live filter/search/scroll state out of an already-rendered list. */
function readLiveState(root: HTMLElement): ListViewState | null {
  const selects = root.querySelectorAll<HTMLSelectElement>('select[data-facet]');
  if (!selects.length) return null;
  const val = (f: Facet): string =>
    root.querySelector<HTMLSelectElement>(`select[data-facet="${f}"]`)?.value ?? '';
  return {
    show: val('show'),
    creator: val('creator'),
    priority: val('priority'),
    query: root.querySelector<HTMLInputElement>('#search-input')?.value ?? '',
    scrollY: scrollTop(),
  };
}

const FACET_VALUE: Record<Facet, (b: BoothRecord) => string> = {
  show: (b) => b.show,
  // Creator groups by the DISPLAYED first name (creatorLabel), not the raw stored
  // value, so "Brandon", "Brandon Penticuff", and any display-name variant collapse
  // to ONE filter option per person — filtering compares in the same label domain.
  creator: (b) => creatorLabel(b.captured_by) || '—',
  priority: (b) => b.priority,
};

export async function renderList(root: HTMLElement, mode: RenderMode = 'fresh'): Promise<void> {
  // Capture the live state BEFORE the await + rebuild: a post-sync re-render must
  // put the user back exactly where they were, and after `innerHTML` it's gone.
  const live = mode === 'preserve' ? readLiveState(root) : null;
  const booths = await listBooths();

  if (booths.length === 0) {
    root.innerHTML = `<div class="empty">No booths captured yet.<br/>Tap <strong>Capture</strong> to log one.</div>`;
    return;
  }

  // 'restore' with nothing remembered (e.g. a reload while on a booth) behaves
  // exactly like 'fresh' — All filters, top of the list.
  const init: ListViewState | null = mode === 'preserve' ? live : mode === 'restore' ? getListState() : null;
  if (mode === 'fresh') clearListState();

  // Distinct options per facet, derived from the data. Priority keeps its natural
  // revisit→order→pass order; show/creator use first-seen order.
  const options: Record<Facet, string[]> = {
    show: distinct(booths, FACET_VALUE.show),
    creator: distinct(booths, FACET_VALUE.creator),
    priority: PRIORITIES.filter((p) => booths.some((b) => b.priority === p)),
  };
  // One selected value per facet ('' = All). Combinable = AND across the three.
  // A remembered value is only re-applied if it's still an option (the booth that
  // was the sole "Jordan" capture may have been deleted while we were away).
  const keep = (f: Facet, v: string | undefined): string =>
    v && options[f].includes(v) ? v : '';
  const selected: Record<Facet, string> = {
    show: keep('show', init?.show),
    creator: keep('creator', init?.creator),
    priority: keep('priority', init?.priority),
  };
  const FACETS: Facet[] = ['show', 'creator', 'priority'];
  // v0.6 free-text search, ANDed on top of the dropdowns. '' = not filtering.
  let query = (init?.query ?? '').trim().toLowerCase();

  root.innerHTML = `<div id="totals"></div><div class="filters" id="filters"></div><div id="list-body"></div>`;
  const totalsEl = root.querySelector<HTMLElement>('#totals')!;
  const filtersEl = root.querySelector<HTMLElement>('#filters')!;
  const bodyEl = root.querySelector<HTMLElement>('#list-body')!;

  const matches = (b: BoothRecord): boolean =>
    FACETS.every((f) => !selected[f] || selected[f] === FACET_VALUE[f](b)) &&
    (!query || haystack(b).includes(query));

  // Three compact native <select>s in one row: Show / Creator / Priority, each
  // defaulting to "All", with an always-visible search field below them. Options
  // are derived from the loaded booths. Built once — the controls hold their own
  // state; a change just re-renders the list body. (v0.6.1: the search field is
  // permanent — the spyglass toggle was dropped, so the selects reclaim that row.)
  function renderFilters(): void {
    const bar = FACETS.map((f) => {
      const opts = [
        `<option value="">${esc(FACET_ALL[f])}</option>`,
        // Creator options are already in the label domain (FACET_VALUE runs
        // creatorLabel), so value and display text are the same string per facet.
        ...options[f].map((v) => `<option value="${esc(v)}">${esc(v)}</option>`),
      ].join('');
      return `<select class="filters__select" data-facet="${f}" aria-label="Filter by ${FACET_LABEL[f].toLowerCase()}">${opts}</select>`;
    }).join('');
    filtersEl.innerHTML = `
      <div class="filters__bar">${bar}</div>
      <div class="filters__search">
        <input type="search" class="filters__search-input" id="search-input"
               placeholder="Search booths&hellip;" aria-label="Search booths"
               autocomplete="off" autocapitalize="none" autocorrect="off" spellcheck="false" />
        <button type="button" class="filters__search-clear" id="search-clear" aria-label="Clear search">&#10005;</button>
      </div>`;

    filtersEl.querySelectorAll<HTMLSelectElement>('select[data-facet]').forEach((sel) => {
      // Re-apply a remembered selection (restore / preserve); '' is "All" anyway.
      sel.value = selected[sel.dataset.facet as Facet];
      sel.addEventListener('change', () => {
        selected[sel.dataset.facet as Facet] = sel.value;
        renderBody();
      });
    });

    const input = filtersEl.querySelector<HTMLInputElement>('#search-input')!;
    input.value = init?.query ?? '';
    let timer: number | undefined;

    // Live filter, debounced like the vendor search so typing stays smooth on a phone.
    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        query = input.value.trim().toLowerCase();
        renderBody();
      }, SEARCH_DEBOUNCE_MS);
    });

    filtersEl.querySelector<HTMLButtonElement>('#search-clear')!.addEventListener('click', () => {
      window.clearTimeout(timer);
      input.value = '';
      input.focus();
      if (query) {
        query = '';
        renderBody();
      }
    });
  }

  function renderBody(): void {
    const shown = booths.filter(matches);
    if (shown.length === 0) {
      bodyEl.innerHTML = query
        ? `<div class="empty empty--filtered">No booths match &ldquo;${esc(query)}&rdquo;.<br/>Try fewer letters, or clear the search.</div>`
        : `<div class="empty empty--filtered">No booths match these filters.<br/>Set the dropdowns back to <strong>All</strong> to see everything.</div>`;
      return;
    }
    bodyEl.innerHTML = `<ul class="booth-list">${shown.map(rowHtml).join('')}</ul>`;
    void attachThumbs(bodyEl, shown);
  }

  // Tapping into a booth is the ONLY way to leave the list "with a place to come
  // back to", so that's where the state is captured — while the DOM is still here
  // and the scroll offset is still the user's. (The Booths tab clears it instead,
  // in app.ts, which is what makes a tab entry land at the top.)
  bodyEl.addEventListener('click', () => {
    const s = readLiveState(root);
    if (s) saveListState(s);
  });

  /** Per-show + grand spend, with the unreadable-values count surfaced, not hidden. */
  function renderTotals(): void {
    const t = computeSpendTotals(booths);
    const open = totalsOpen() ? ' open' : '';
    const note = unparsedNote(t.unparsed);
    const rows = t.shows
      .map(
        (s) => `
          <div class="totals__row">
            <span class="totals__show">${esc(s.show)}</span>
            <span class="totals__count">${s.booths} ${s.booths === 1 ? 'booth' : 'booths'}${
              s.unparsed ? ` · ${s.unparsed} unreadable` : ''
            }</span>
            <span class="totals__amt">${money(s.total)}</span>
          </div>`,
      )
      .join('');
    totalsEl.innerHTML = `
      <details class="totals" id="totals-details"${open}>
        <summary class="totals__summary">
          <span class="totals__label">Spend</span>
          <span class="totals__grand">${money(t.grand)}</span>
          <span class="totals__count">${t.booths} ${t.booths === 1 ? 'booth' : 'booths'}</span>
        </summary>
        <div class="totals__body">
          ${rows}
          <div class="totals__row totals__row--grand">
            <span class="totals__show">All shows</span>
            <span class="totals__count"></span>
            <span class="totals__amt">${money(t.grand)}</span>
          </div>
          ${note ? `<div class="totals__warn">${esc(note)}</div>` : ''}
        </div>
      </details>`;
    if (note) {
      // Visible without expanding: a total that's missing entries has to say so.
      const sum = totalsEl.querySelector<HTMLElement>('.totals__summary')!;
      const flag = document.createElement('span');
      flag.className = 'totals__flag';
      flag.textContent = `${t.unparsed} not counted`;
      sum.appendChild(flag);
    }
    totalsEl
      .querySelector<HTMLDetailsElement>('#totals-details')!
      .addEventListener('toggle', (e) => setTotalsOpen((e.currentTarget as HTMLDetailsElement).open));
  }

  renderTotals();
  renderFilters();
  renderBody();
  // Fresh entry → top; restore/preserve → back where the user was.
  scrollTo(init?.scrollY ?? 0);
}

// Whether the totals block is expanded — remembered per device so the floor team
// can leave it open (or shut) without re-tapping it on every visit to the tab.
const TOTALS_OPEN_KEY = 'bt-totals-open';

function totalsOpen(): boolean {
  try {
    return localStorage.getItem(TOTALS_OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function setTotalsOpen(open: boolean): void {
  try {
    localStorage.setItem(TOTALS_OPEN_KEY, open ? '1' : '0');
  } catch {
    /* storage blocked (private mode) — session-only is acceptable */
  }
}

const FACET_LABEL: Record<Facet, string> = { show: 'Show', creator: 'Creator', priority: 'Priority' };
const FACET_ALL: Record<Facet, string> = { show: 'All shows', creator: 'All creators', priority: 'All priorities' };

/**
 * Creator display AND grouping key: the first token of the stored attribution,
 * Title-cased — "Brandon Penticuff" -> "Brandon". Both the filter options and the
 * match run through this (v0.5.3), so one person = one option even when records
 * mix "Brandon" / "Brandon Penticuff". A record still holding a legacy email login
 * (pre-migration local copy; no space, no parseable first name) falls back to the
 * raw value as its own option rather than a mangled "Bpenticuff@...".
 */
function creatorLabel(raw: string): string {
  const v = (raw ?? '').trim();
  if (!v) return v;
  const first = v.split(/\s+/)[0];
  if (first.includes('@')) return v; // legacy email login - show whatever's there
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

const SEARCH_DEBOUNCE_MS = 150;

/**
 * Lower-cased searchable text for a booth: exactly the fields the user can SEE
 * (list row + detail form), joined. Deliberately EXCLUDES `category_tags` (hidden
 * in v0.6) and every internal field - ids, timestamps, sync status, blob urls - so
 * search only ever matches something the user could have read on screen. Creator is
 * indexed as its displayed first name AND its raw value, so "brandon" matches
 * whichever form the record holds. Cached per record (the list re-filters on every
 * keystroke; records are immutable for the life of this render).
 */
const haystackCache = new WeakMap<BoothRecord, string>();
function haystack(b: BoothRecord): string {
  let h = haystackCache.get(b);
  if (h === undefined) {
    h = [
      b.booth_number,
      b.vendor_name,
      b.summary,
      b.ships_from,
      b.vendor_contact,
      b.case_packs,
      b.size_run,
      b.total_spent,
      b.notes,
      b.show,
      b.priority,
      creatorLabel(b.captured_by),
      b.captured_by,
    ]
      .filter(Boolean)
      .join('   ')
      .toLowerCase();
    haystackCache.set(b, h);
  }
  return h;
}

function distinct(booths: BoothRecord[], pick: (b: BoothRecord) => string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const b of booths) {
    const v = pick(b);
    if (v && !seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

// Lazily attach the first photo thumbnail per booth. Prefer local bytes; for a
// pulled remote booth (bytes not on this device) fetch the thumbnail from its
// blob_url over the authenticated /api/photos path, else leave a placeholder.
async function attachThumbs(scope: HTMLElement, booths: BoothRecord[]): Promise<void> {
  await Promise.all(
    booths.map(async (b) => {
      const img = scope.querySelector<HTMLImageElement>(`[data-thumb="${b.id}"]`);
      if (!img) return;

      const photos = await getPhotosForBooth(b.id);
      if (photos.length) {
        img.src = URL.createObjectURL(photoBlob(photos[0]));
        return;
      }

      const ref = b.photos.find((p) => p.blob_url);
      if (!ref?.blob_url) return; // no photo at all - neutral placeholder background
      try {
        const blob = await fetchPhoto(ref.blob_url);
        img.src = URL.createObjectURL(blob);
      } catch {
        // Signed-out/offline/404 - leave the placeholder rather than a broken image.
      }
    }),
  );
}

function rowHtml(b: BoothRecord): string {
  const when = new Date(b.captured_at).toLocaleString();
  return `
    <li>
     <a class="booth-row" href="#/booth/${esc(b.id)}">
      <div class="booth-row__thumb"><img data-thumb="${b.id}" alt="" /></div>
      <div class="booth-row__body">
        <div class="booth-row__top">
          <strong>#${esc(b.booth_number)}</strong>
          <span class="chip chip--${b.priority}">${b.priority}</span>
          <span class="chip chip--show">${esc(b.show)}</span>
        </div>
        <div class="booth-row__vendor">${esc(b.vendor_name) || '&mdash;'}</div>
        ${b.summary ? `<div class="booth-row__summary">${esc(b.summary)}</div>` : ''}
        <div class="booth-row__meta">
          <span class="badge badge--${b.sync_status}">${b.sync_status === 'local' ? 'on device' : 'synced'}</span>
          <span>${when}</span>
        </div>
      </div>
     </a>
    </li>`;
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}
